import { User, onAuthStateChanged } from "firebase/auth";
import LogRocket from "logrocket";
import React, { createContext, useContext, useEffect, useState } from "react";
import { components } from "../api/types";
import { API_ENDPOINT } from "../config";
import { auth } from "../firebase/firebase";
import { logout } from "../firebase/firebaseAuth";

interface AuthContextType {
  user: User | null;
  logout: () => Promise<void>;
  authChecked: boolean;
  mfaRequired: boolean;
  setMfaRequired: (required: boolean) => void;
}

export type MFARequiredResponse = components["schemas"]["MFARequiredResponse"];
export type MFARequestRequest =
  components["schemas"]["Body_request_otp_v1_mfa_request_post"];
export type MFARequestResponse = components["schemas"]["MFARequiredResponse"];
export type MFAVerifyRequest =
  components["schemas"]["Body_verify_mfa_v1_mfa_verify_post"];
export type MFAVerifyResponse = components["schemas"]["MFAVerifyResponse"];

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState<boolean>(false);
  const [mfaRequired, setMfaRequired] = useState(false);

  useEffect(() => {
    let tokenRefreshInterval: NodeJS.Timeout;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          const token = await user.getIdToken(true);
          // localStorage.setItem("idToken", token);

          const response = await fetch(`${API_ENDPOINT}/v1/mfa/required`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });

          if (!response.ok) {
            throw new Error("Failed to check MFA status");
          }

          const mfaStatus = (
            (await response.json()) as { mfa_required: boolean }
          ).mfa_required;

          console.info("MFA required:", mfaStatus);
          setUser(user);
          setMfaRequired(mfaStatus);

          LogRocket.identify(user.uid, {
            name: user.displayName ?? "",
            email: user.email ?? "",
          });
        } else {
          setUser(null);
          setMfaRequired(false);
        }
      } catch (error) {
        console.error("Error getting token:", error);
        setUser(null);
      } finally {
        setAuthChecked(true);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const values = {
    user,
    logout,
    authChecked,
    mfaRequired,
    setMfaRequired,
  };

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
